import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PwaInstall from './PwaInstall';

const API_BASE = process.env.REACT_APP_API_URL || '';
const APP_URL = process.env.REACT_APP_URL || 'https://app.traindaily.com';
const LOGO = '/dailyreps3.png';

export default function CoachOnboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const sessionId = searchParams.get('session_id');
  const billingModelParam = searchParams.get('billing_model');
  const isResume = searchParams.get('resume') === '1';
  const isClubRoute = location.pathname.startsWith('/club/');
  // No-Stripe flow: setup path without a Stripe session (parent_pays OR club_pays account-first)
  const isNoStripeFlow = (location.pathname === '/coaches/setup' || location.pathname === '/club/setup') && !sessionId;
  const { loginStaff } = useAuth();

  const [step, setStep] = useState(1);
  const [maxStep, setMaxStep] = useState(1);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [sessionError, setSessionError] = useState('');
  const [loading, setLoading] = useState(false);

  // Step 1: Account creation
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [teamName, setTeamName] = useState('');
  const [clubName, setClubName] = useState('');
  const [isClub, setIsClub] = useState(isClubRoute);
  const [accountError, setAccountError] = useState('');
  const [token, setToken] = useState(null);
  const [timezone, setTimezone] = useState(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'America/New_York'; }
  });

  // Step 2: Team details
  const [team, setTeam] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);

  const totalSteps = isClubRoute ? 2 : 3;

  // Affiliate tracking
  useEffect(() => {
    const s = document.createElement('script');
    s.src = 'https://affiliate-platform-production-f4fa.up.railway.app/track.js?pid=cmtdislyw024r2tx3gmysyb0g&v=1';
    s.async = true;
    document.head.appendChild(s);
    return () => { document.head.removeChild(s); };
  }, []);

  // Affiliate tracking (step 2 conversion)
  useEffect(() => {
    if (step !== 2) return;
    const s = document.createElement('script');
    s.src = 'https://affiliate-platform-production-f4fa.up.railway.app/track.js?pid=cmtdislyw024r2tx3gmysyb0g&v=2';
    s.async = true;
    document.head.appendChild(s);
    return () => { document.head.removeChild(s); };
  }, [step]);

  // Affiliate tracking (sale confirmation)
  useEffect(() => {
    if (!sessionId) return;
    const s = document.createElement('script');
    s.src = 'https://affiliate-platform-production-f4fa.up.railway.app/track.js?pid=cmtdislyw024r2tx3gmysyb0g&v=3';
    s.async = true;
    document.head.appendChild(s);
    return () => { document.head.removeChild(s); };
  }, [sessionId]);

  // Resume mode: if user is already logged in (coming back to finish wizard), skip step 1
  useEffect(() => {
    if (isResume && localStorage.getItem('token')) {
      setStep(2);
      setMaxStep(2);
    }
  }, [isResume]);

  // Track the farthest step reached
  const goToStep = (s) => {
    if (s >= 1 && s <= totalSteps && s <= maxStep) {
      setStep(s);
    }
  };

  const advanceStep = (s) => {
    setStep(s);
    setMaxStep(prev => Math.max(prev, s));
  };

  // Load session info (for Stripe checkout flow)
  useEffect(() => {
    if (isNoStripeFlow || !sessionId) return;
    fetch(`${API_BASE}/api/order/session/${sessionId}`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error('Invalid session')))
      .then(data => {
        setSessionInfo(data);
        setEmail(data.email || '');
      })
      .catch(err => setSessionError(err.message));
  }, [sessionId, isNoStripeFlow]);

  // Step 1: Create account
  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setAccountError('');
    if (password !== confirmPassword) { setAccountError('Passwords do not match'); return; }
    if (password.length < 8) { setAccountError('Password must be at least 8 characters'); return; }
    if (!isClubRoute && !teamName.trim()) { setAccountError('Team name is required'); return; }
    setLoading(true);
    try {
      let data;
      if (isNoStripeFlow) {
        const body = {
          email, password, first_name: firstName, last_name: lastName,
          team_name: isClubRoute ? undefined : teamName, club_name: isClub ? clubName : undefined,
          timezone,
        };
        // Pass billing_model for club_pays signups
        if (billingModelParam === 'club_pays') {
          body.billing_model = 'club_pays';
        }
        const affiliateRef = searchParams.get('aff');
        if (affiliateRef) body.affiliate_ref = affiliateRef;
        const res = await fetch(`${API_BASE}/api/order/complete-coach-free`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Account creation failed');
      } else {
        const res = await fetch(`${API_BASE}/api/order/complete-coach`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: sessionId, password, first_name: firstName, last_name: lastName,
            team_name: teamName, club_name: isClub ? clubName : undefined,
            timezone,
          }),
        });
        data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Account creation failed');
      }
      setToken(data.token);
      setTeam(data.team);
      localStorage.setItem('token', data.token);
      if (data.already_completed) {
        navigate(isClubRoute ? '/club' : '/admin');
        return;
      }
      advanceStep(2);
    } catch (err) {
      setAccountError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const parentInviteUrl = team ? `${APP_URL}/parents/join/${team.join_code}` : '';

  const handleCopyLink = () => {
    navigator.clipboard.writeText(parentInviteUrl).then(() => {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    });
  };

  const handleDone = async () => {
    try {
      if (email && password) {
        await loginStaff(email, password);
      }
    } catch (e) {
      // Token already set
    }
    // Club admins go through the full onboarding wizard next;
    // navigate to /login so LoginRedirect checks onboarding_completed
    // and redirects to /club/setup?resume=1
    navigate('/login');
  };

  if (sessionError) {
    return (
      <div className="order-page">
        <header className="order-header"><div className="order-wrap">
          <div className="order-logo-row">
            <img src={LOGO} alt="Train Daily" className="order-logo" />
            <span className="order-brand">Train Daily</span>
          </div>
        </div></header>
        <main className="order-main"><div className="order-wrap">
          <div className="order-error-card"><h2>Oops</h2><p>{sessionError}</p></div>
        </div></main>
      </div>
    );
  }

  if (!isNoStripeFlow && !isResume && !sessionInfo && step === 1) {
    return <div className="order-page"><div className="loading"><div className="spinner" /></div></div>;
  }

  return (
    <div className="order-page">
      <header className="order-header"><div className="order-wrap">
        <div className="order-logo-row">
          <img src={LOGO} alt="Train Daily" className="order-logo" />
          <span className="order-brand">Train Daily</span>
        </div>
      </div></header>

      <main className="order-main"><div className="order-wrap">
        {/* Step navigation with back/forward arrows */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <button
            onClick={() => goToStep(step - 1)}
            disabled={step <= 1 || (isResume && step <= 2)}
            type="button"
            style={{ background: 'none', border: 'none', cursor: (step > 1 && !(isResume && step <= 2)) ? 'pointer' : 'default', opacity: (step > 1 && !(isResume && step <= 2)) ? 1 : 0.25, fontSize: 22, color: '#0d1b4c', padding: '4px 8px' }}
            aria-label="Previous step"
          >
            <i className="fas fa-arrow-left"></i>
          </button>

          <div className="onboarding-progress" style={{ margin: 0 }}>
            {Array.from({ length: totalSteps }, (_, i) => i + 1).map(s => (
              <div
                key={s}
                className={`onboarding-dot ${step >= s ? 'active' : ''} ${step === s ? 'current' : ''}`}
                onClick={() => goToStep(s)}
                style={{ cursor: s <= maxStep ? 'pointer' : 'default' }}
                role="button"
                tabIndex={0}
              >
                {step > s ? <i className="fas fa-check"></i> : s}
              </div>
            ))}
          </div>

          <button
            onClick={() => goToStep(step + 1)}
            disabled={step >= maxStep}
            type="button"
            style={{ background: 'none', border: 'none', cursor: step < maxStep ? 'pointer' : 'default', opacity: step < maxStep ? 1 : 0.25, fontSize: 22, color: '#0d1b4c', padding: '4px 8px' }}
            aria-label="Next step"
          >
            <i className="fas fa-arrow-right"></i>
          </button>
        </div>

        {/* Step 1: Create Account */}
        {step === 1 && (
          <div className="onboarding-card">
            <h2>Create Your Account</h2>
            <p className="onboarding-subtitle">
              {isClubRoute ? "Set up your club admin account." : isNoStripeFlow ? "Set up your free coach account." : "Set up your coach account and team."}
            </p>
            <form onSubmit={handleCreateAccount}>
              {accountError && <div className="order-error">{accountError}</div>}
              <div className="onboarding-field">
                <label>Email</label>
                {isNoStripeFlow ? (
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="onboarding-input" autoCapitalize="none" autoCorrect="off" />
                ) : (
                  <input type="email" value={email} readOnly className="onboarding-input readonly" />
                )}
              </div>
              <div className="onboarding-row">
                <div className="onboarding-field">
                  <label>First Name</label>
                  <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} required className="onboarding-input" />
                </div>
                <div className="onboarding-field">
                  <label>Last Name</label>
                  <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} required className="onboarding-input" />
                </div>
              </div>
              <div className="onboarding-field">
                <label>Password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} className="onboarding-input" />
              </div>
              <div className="onboarding-field">
                <label>Confirm Password</label>
                <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required className="onboarding-input" />
              </div>

              {isClubRoute && (
                <div className="onboarding-field" style={{ marginTop: 16 }}>
                  <label>Club Name</label>
                  <input type="text" value={clubName} onChange={e => setClubName(e.target.value)} required className="onboarding-input" placeholder="e.g. Metro FC" />
                </div>
              )}

              {!isClubRoute && (
                <div className="onboarding-field" style={{ marginTop: 16 }}>
                  <label>Team Name</label>
                  <input type="text" value={teamName} onChange={e => setTeamName(e.target.value)} required className="onboarding-input" placeholder="e.g. U12 Lightning" />
                </div>
              )}

              {!isClubRoute && (
                <>
                  <label className="onboarding-consent-label" style={{ marginTop: 12 }}>
                    <input type="checkbox" checked={isClub} onChange={e => setIsClub(e.target.checked)} />
                    <span>This is part of a club (multiple teams)</span>
                  </label>

                  {isClub && (
                    <div className="onboarding-field">
                      <label>Club Name</label>
                      <input type="text" value={clubName} onChange={e => setClubName(e.target.value)} required className="onboarding-input" placeholder="e.g. Metro FC" />
                    </div>
                  )}
                </>
              )}

              <div className="onboarding-field" style={{ marginTop: 16 }}>
                <label>Timezone</label>
                <select value={timezone} onChange={e => setTimezone(e.target.value)} className="onboarding-input">
                  {Intl.supportedValuesOf ? Intl.supportedValuesOf('timeZone').map(tz => (
                    <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
                  )) : [
                    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
                    'America/Anchorage', 'Pacific/Honolulu', 'America/Phoenix',
                    'America/Toronto', 'America/Vancouver', 'America/Edmonton',
                    'Europe/London', 'Europe/Paris', 'Europe/Berlin',
                    'Asia/Tokyo', 'Asia/Shanghai', 'Australia/Sydney',
                  ].map(tz => (
                    <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>

              <button className="btn btn-primary order-btn" type="submit" disabled={loading} style={{ width: '100%', marginTop: 16 }}>
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>
          </div>
        )}

        {/* Step 2: Team Details (coaches only) */}
        {step === 2 && !isClubRoute && (
          <div className="onboarding-card">
            <h2>Your Team is Ready</h2>
            {isNoStripeFlow && team && (
              <div className="onboarding-invites" style={{ marginTop: 24 }}>
                <h3>Parent Invite Link</h3>
                <p style={{ fontSize: '0.9rem', color: '#52627a', marginBottom: 12 }}>
                  Share this link with parents. They'll use it to sign up their player.
                </p>
                <div className="invite-codes">
                  <div className="invite-code-row">
                    <code className="invite-code-url">{parentInviteUrl}</code>
                    <button className="btn btn-sm btn-outline" onClick={handleCopyLink} type="button">
                      {copiedLink ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <button className="btn btn-primary order-btn" onClick={() => advanceStep(3)} style={{ width: '100%', marginTop: 20 }} type="button">
              Continue
            </button>
          </div>
        )}

        {/* PWA Install (last step) */}
        {step === totalSteps && (
          <div className="onboarding-card">
            {isClubRoute && !isResume && (
              <h2 style={{ marginBottom: 16 }}>Welcome to Train Daily, {clubName}!</h2>
            )}
            {isClubRoute && isResume && (
              <h2 style={{ marginBottom: 16 }}>Welcome back!</h2>
            )}
            <PwaInstall onDone={handleDone} doneLabel="Go to Dashboard" />
          </div>
        )}
      </div></main>
    </div>
  );
}
